import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle, Clock, FileText, Send, Shield, 
  AlertTriangle, ArrowRight, Phone, Mail, HelpCircle, ExternalLink, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageTracking } from '../hooks/usePageTracking';
import { safeJsonLd } from '../lib/safeJsonLd';
import { PLATFORM_ENHANCEMENTS } from '../data/platformEnhancements';

// Platform-specific data
const PLATFORM_DATA = {
  instagram: {
    name: 'Instagram',
    parent: 'Meta',
    color: 'from-pink-500 to-purple-600',
    icon: '📸',
    metaTitle: 'Instagram Account Suspended? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'Instagram account suspended? We help prepare appeal documentation. Independent service - not affiliated with Meta. No guarantees.',
    heroTitle: 'Instagram Account Suspended?',
    heroSubtitle: 'We help prepare organized documentation for your appeal submission',
    commonReasons: [
      'Violation of Community Guidelines',
      'Suspicious activity or hacked account',
      'Impersonation allegations',
      'Intellectual property claims',
      'Age verification issues',
      'Automated behavior detection'
    ],
    processTime: 'Case-dependent',
    highlight: 'Documentation Support'
  },
  facebook: {
    name: 'Facebook',
    parent: 'Meta',
    color: 'from-blue-500 to-blue-700',
    icon: '👤',
    metaTitle: 'Facebook Account Disabled? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'Facebook account disabled? We help prepare appeal documentation. Independent service - not affiliated with Meta. No guarantees.',
    heroTitle: 'Facebook Account Disabled?',
    heroSubtitle: 'We help prepare documentation for your Facebook appeal',
    commonReasons: [
      'Community Standards violation',
      'Identity verification required',
      'Suspicious login activity',
      'Ad account policy violations',
      'Page unpublished',
      'Marketplace restrictions'
    ],
    processTime: 'Case-dependent',
    highlight: 'Documentation Support'
  },
  tiktok: {
    name: 'TikTok',
    parent: 'ByteDance',
    color: 'from-black to-pink-500',
    icon: '🎵',
    metaTitle: 'TikTok Account Banned? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'TikTok account banned? We help prepare appeal documentation. Independent service - not affiliated with TikTok. No guarantees.',
    heroTitle: 'TikTok Account Banned?',
    heroSubtitle: 'We help prepare documentation for your TikTok appeal',
    commonReasons: [
      'Community Guidelines violation',
      'Multiple violations warning',
      'Minor safety concerns',
      'Spam or fake engagement',
      'Creator Fund eligibility issues',
      'LIVE access revoked'
    ],
    processTime: 'Case-dependent',
    highlight: 'Documentation Support'
  },
  youtube: {
    name: 'YouTube',
    parent: 'Google',
    color: 'from-red-500 to-red-700',
    icon: '▶️',
    metaTitle: 'YouTube Channel Terminated? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'YouTube channel terminated? We help prepare appeal documentation. Independent service - not affiliated with Google. No guarantees.',
    heroTitle: 'YouTube Channel Terminated?',
    heroSubtitle: 'We help prepare documentation for your YouTube appeal',
    commonReasons: [
      'Community Guidelines strikes',
      'Copyright strikes',
      'Terms of Service violation',
      'Spam/deceptive practices',
      'Monetization suspended',
      'AdSense account issues'
    ],
    processTime: 'Case-dependent',
    highlight: 'Documentation Support'
  },
  twitter: {
    name: 'X (Twitter)',
    parent: 'X Corp',
    color: 'from-gray-800 to-black',
    icon: '𝕏',
    metaTitle: 'X/Twitter Account Suspended? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'X account suspended? We help prepare appeal documentation. Independent service - not affiliated with X Corp. No guarantees.',
    heroTitle: 'X Account Suspended?',
    heroSubtitle: 'We help prepare documentation for your X appeal',
    commonReasons: [
      'Platform manipulation',
      'Abusive behavior',
      'Spam detection',
      'Evading suspension',
      'Verification issues',
      'API/automation violations'
    ],
    processTime: 'Case-dependent',
    highlight: 'Documentation Support'
  },
  linkedin: {
    name: 'LinkedIn',
    parent: 'Microsoft',
    color: 'from-blue-600 to-blue-800',
    icon: '💼',
    metaTitle: 'LinkedIn Account Restricted? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'LinkedIn account restricted? We help prepare appeal documentation. Independent service - not affiliated with Microsoft. No guarantees.',
    heroTitle: 'LinkedIn Account Restricted?',
    heroSubtitle: 'We help prepare documentation for your LinkedIn appeal',
    commonReasons: [
      'Connection request limits',
      'Profile restrictions',
      'Sales Navigator issues',
      'Recruiter account problems',
      'Content removal',
      'Identity verification'
    ],
    processTime: 'Case-dependent',
    highlight: 'Documentation Support'
  },
  ubereats: {
    name: 'Uber Eats',
    parent: 'Uber',
    color: 'from-green-500 to-green-700',
    icon: '🍔',
    metaTitle: 'Uber Eats Account Deactivated? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'Uber Eats account deactivated? We help prepare appeal documentation. Independent service - not affiliated with Uber. No guarantees.',
    heroTitle: 'Uber Eats Account Deactivated?',
    heroSubtitle: 'We help prepare documentation for your Uber Eats appeal',
    commonReasons: [
      'Low ratings or customer complaints',
      'Delivery issues or cancellations',
      'Background check concerns',
      'Account fraud allegations',
      'Policy violations',
      'Documentation or verification issues'
    ],
    processTime: 'Case-dependent',
    highlight: 'Drivers & Restaurants'
  },
  'google-business': {
    name: 'Google Business Profile',
    parent: 'Google',
    color: 'from-blue-500 to-blue-700',
    icon: 'G',
    metaTitle: 'Google Business Profile Suspended? Appeal Documentation Help | Social Dispute Co',
    metaDescription: 'Google Business Profile suspended or disabled? Independent appeal documentation service. We map your case to Google\'s published guidelines.',
    heroTitle: 'Google Business Profile Suspended?',
    heroSubtitle: 'We help prepare documentation for your GBP reinstatement request',
    commonReasons: [
      'Guideline violation (suspect business model)',
      'Address verification issues',
      'Duplicate listing detection',
      'Category mismatch',
      'Service-area boundary issues',
      'Keyword stuffing in business name',
      'Multiple location operator flags',
      'Suspicious review patterns'
    ],
    processTime: 'Case-dependent',
    highlight: 'Local SEO Recovery'
  },
  'facebook-business-manager': {
    name: 'Facebook Business Manager',
    parent: 'Meta',
    color: 'from-blue-500 to-blue-700',
    icon: '🏢',
    metaTitle: 'Facebook Business Manager Disabled? Appeal Documentation | Social Dispute Co',
    metaDescription: 'Facebook Business Manager disabled? We help businesses prepare appeal documentation for BM cascades (ad accounts, Pages, pixels, catalogs). No affiliation with Meta.',
    heroTitle: 'Facebook Business Manager Disabled?',
    heroSubtitle: 'BM disables cascade across ad accounts, Pages, pixels & catalogs — we map the full cascade and prepare your appeal',
    commonReasons: [
      'Advertising Standards violation (cascades to BM)',
      'Account quality / customer feedback score',
      'Business Verification failure',
      'Connected-account cascade enforcement',
      'Coordinated Inauthentic Behavior (CIB) flag',
      'Repeat policy violations across linked assets',
      'Domain verification disputes',
      'Billing or chargeback issues'
    ],
    processTime: 'Case-dependent',
    highlight: 'Business Critical'
  },
  'instagram-ad-account': {
    name: 'Instagram Ad Account',
    parent: 'Meta',
    color: 'from-purple-500 to-pink-500',
    icon: '📣',
    metaTitle: 'Instagram Ad Account Disabled? Appeal Documentation | Social Dispute Co',
    metaDescription: 'Instagram ad account disabled or restricted? We help prepare appeal documentation citing Meta\'s Advertising Standards. Independent service, no guarantees.',
    heroTitle: 'Instagram Ad Account Disabled?',
    heroSubtitle: 'We help prepare documentation citing Meta\'s Advertising Standards for your IG ad account appeal',
    commonReasons: [
      'Advertising Standards violation (personal attributes targeting)',
      'Misleading or unverified claims in creative',
      'Restricted-category content (financial, health, political)',
      'Account quality / high disapproval rate',
      'Linked Business Manager cascade',
      'Billing failure or chargeback signals',
      'Pixel / Conversion API mismatch',
      'Domain ownership / verification conflict'
    ],
    processTime: 'Case-dependent',
    highlight: 'Revenue Critical'
  }
};

const PlatformServicePage = ({ platform }) => {
  const data = PLATFORM_DATA[platform];
  usePageTracking(data ? `Services: ${data.name}` : "Services", { category: "service", value: 399 });

  // Canonical URL map — used to consolidate any aliases onto a single URL.
  // Both /services/twitter-account-appeal and /services/x-account-appeal
  // render with platform="twitter", but we want X to be the canonical.
  const CANONICAL_PATH = {
    instagram: '/services/instagram-account-appeal',
    facebook: '/services/facebook-account-appeal',
    tiktok: '/services/tiktok-account-appeal',
    youtube: '/services/youtube-account-appeal',
    twitter: '/services/x-account-appeal',
    linkedin: '/services/linkedin-account-appeal',
    ubereats: '/services/ubereats-account-appeal',
    'google-business': '/services/google-business-profile-suspended',
    'facebook-business-manager': '/services/facebook-business-manager-disabled',
    'instagram-ad-account': '/services/instagram-ad-account-disabled',
  };

  // Update document head for SEO
  useEffect(() => {
    if (!data) return;

    document.title = data.metaTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', data.metaDescription);
    }

    const canonicalPath = CANONICAL_PATH[platform] || `/services/${platform}-account-appeal`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://socialdisputeco.com${canonicalPath}`);
    }

    return () => {
      document.title = 'Social Media Account Appeal Documentation | Social Dispute Co.';
    };
  }, [data, platform]);
  
  if (!data) {
    return <div>Platform not found</div>;
  }

  // Merge in enriched per-platform content (FAQs, evidence, related articles, etc.)
  const enriched = PLATFORM_ENHANCEMENTS[platform] || null;

  // Schema markup for this page — Service + FAQPage when enriched data is available
  const canonicalPath = CANONICAL_PATH[platform] || `/services/${platform}-account-appeal`;
  const canonicalUrl = `https://socialdisputeco.com${canonicalPath}`;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${data.name} Appeal Documentation Support`,
    "description": (enriched && enriched.metaDescriptionOverride) || data.metaDescription,
    "url": canonicalUrl,
    "provider": {
      "@type": "Organization",
      "name": "Social Dispute Co.",
      "url": "https://socialdisputeco.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Honolulu",
        "addressRegion": "HI",
        "addressCountry": "US"
      }
    },
    "areaServed": "United States",
    "serviceType": "Appeal Documentation Preparation",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${data.name} Appeal Service Tiers`,
      "itemListElement": [
        { "@type": "Offer", "name": "Case Review", "price": "99", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Appeal Package", "price": "399", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Priority Support", "price": "499", "priceCurrency": "USD" }
      ]
    }
  };

  const faqSchema = enriched && enriched.faqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": enriched.faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      {/* Schema */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger -- JSON-LD is the W3C-recommended pattern; content is escaped via safeJsonLd
        dangerouslySetInnerHTML={{ __html: safeJsonLd(serviceSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger -- JSON-LD is the W3C-recommended pattern; content is escaped via safeJsonLd
          dangerouslySetInnerHTML={{ __html: safeJsonLd(faqSchema) }}
        />
      )}
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className={`pt-32 pb-16 bg-gradient-to-br ${data.color} relative overflow-hidden`}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="max-w-5xl mx-auto px-5 md:px-10 relative z-10">
            <div className="text-center">
              <span className="text-6xl mb-6 block">{data.icon}</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase leading-[0.9] mb-6">
                {data.heroTitle}
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-2xl mx-auto">
                {data.heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/#intake">
                  <Button 
                    size="lg"
                    className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold text-lg px-8"
                  >
                    Start Your Case
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:808-301-2134">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white/10 font-bold text-lg px-8"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    808-301-2134
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#302f2c] py-6 border-y border-[#3f4816]/50">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl md:text-3xl font-black text-[#d9fb06]">{data.highlight}</p>
                <p className="text-sm text-[#888680]">Specialty</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-black text-[#d9fb06]">{data.processTime}</p>
                <p className="text-sm text-[#888680]">Timeline</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-black text-[#d9fb06]">USA</p>
                <p className="text-sm text-[#888680]">Based Service</p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Reasons Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase text-center mb-12">
              Common {data.name} Suspension Reasons
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.commonReasons.map((reason) => (
                <div 
                  key={reason}
                  className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-4 flex items-center gap-3"
                >
                  <AlertTriangle className="w-5 h-5 text-[#d9fb06] flex-shrink-0" />
                  <span className="text-[#dfddd6]">{reason}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-[#888680] mt-8">
              Don't see your situation? We handle all types of {data.name} account issues.{' '}
              <Link to="/contact" className="text-[#d9fb06] hover:underline">Contact us</Link> for a free case evaluation.
            </p>
          </div>
        </section>

        {/* How We Help Section */}
        <section className="py-16 md:py-24 bg-[#302f2c]">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase text-center mb-12">
              How We Help With {data.name} Appeals
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#d9fb06]/20 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-[#d9fb06]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#dfddd6] mb-2">Professional Documentation</h3>
                    <p className="text-[#888680]">
                      We prepare structured documentation for submission through official platform channels,
                      not generic appeal templates.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#d9fb06]/20 flex items-center justify-center flex-shrink-0">
                    <Send className="w-6 h-6 text-[#d9fb06]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#dfddd6] mb-2">Documentation Support</h3>
                    <p className="text-[#888680]">
                      We help organize documentation for submission through official {data.parent} appeal channels.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#d9fb06]/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-[#d9fb06]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#dfddd6] mb-2">Policy-Aware Documentation</h3>
                    <p className="text-[#888680]">
                      Documentation structured with relevant policy context to support 
                      your appeal submission through official channels.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#d9fb06]/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#d9fb06]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#dfddd6] mb-2">Dedicated Case Tracking</h3>
                    <p className="text-[#888680]">
                      Track your case status 24/7 with your unique Case ID. 
                      Full transparency throughout the process.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* === ENRICHED CONTENT (intro, deep reasons, FAQ, related) === */}
        {enriched && (
          <>
            {/* Free Analyzer prompt — high-converting top-of-page hook */}
            <section className="py-10 bg-gradient-to-r from-blue-600/15 via-blue-500/5 to-transparent border-b border-[#3f4816]/30">
              <div className="max-w-5xl mx-auto px-5 md:px-10">
                <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <p className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium mb-2">
                      <Sparkles className="w-3.5 h-3.5" />
                      Free · 60 seconds
                    </p>
                    <h2 className="text-xl md:text-2xl font-bold text-[#dfddd6] mb-1">
                      Not sure why your {data.name} account was disabled?
                    </h2>
                    <p className="text-sm text-[#888680]">
                      Paste your suspension notice and our free analyzer will identify the most likely policy category and recommended next step.
                    </p>
                  </div>
                  <Link
                    to="/analyze"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#d9fb06] text-[#1a1c1b] px-6 py-3 font-bold text-sm whitespace-nowrap hover:bg-[#d9fb06]/90 transition"
                    data-testid={`platform-${platform}-analyzer-cta`}
                  >
                    Analyze My Suspension
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </section>

            {/* Intro narrative for SEO + AEO depth */}
            <section className="py-12 md:py-16">
              <div className="max-w-3xl mx-auto px-5 md:px-10">
                <p className="text-lg text-[#dfddd6] leading-relaxed">
                  {enriched.introCopy}
                </p>
              </div>
            </section>

            {/* Deep "Why was my account disabled" — replaces the thin commonReasons list */}
            <section className="py-12 md:py-16 bg-[#302f2c]" data-testid={`platform-${platform}-why-disabled`}>
              <div className="max-w-5xl mx-auto px-5 md:px-10">
                <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-3">
                  Why {data.name} Accounts Get Disabled
                </h2>
                <p className="text-sm text-[#888680] mb-10 max-w-2xl">
                  Sourced from {data.parent}'s own published policy documentation. We've grouped these by the categories that map to {data.parent}'s appeal review system.
                </p>
                <div className="grid md:grid-cols-2 gap-5">
                  {enriched.whyDisabled.map((reason) => (
                    <div
                      key={reason.title}
                      className="bg-[#1a1c1b] border border-[#3f4816]/40 rounded-xl p-5"
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <AlertTriangle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                        <h3 className="text-base font-bold text-[#dfddd6] leading-tight">{reason.title}</h3>
                      </div>
                      <p className="text-sm text-[#888680] leading-relaxed pl-8">{reason.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Evidence checklist */}
            <section className="py-12 md:py-16" data-testid={`platform-${platform}-evidence`}>
              <div className="max-w-3xl mx-auto px-5 md:px-10">
                <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-3">
                  Evidence Reviewers Typically Need
                </h2>
                <p className="text-sm text-[#888680] mb-8">
                  Whether you DIY the appeal or engage us, these are the items {data.parent}'s reviewers tend to look for in {data.name} cases.
                </p>
                <ul className="space-y-3">
                  {enriched.evidenceTypical.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[#dfddd6]">
                      <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                      <span className="text-sm md:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 rounded-xl border border-[#3f4816]/40 bg-[#302f2c] p-5">
                  <p className="text-xs uppercase tracking-wider text-[#888680] mb-2">Official {data.name} appeal channel</p>
                  <a
                    href={enriched.appealChannel}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#d9fb06] hover:underline text-sm font-mono break-all"
                  >
                    {enriched.appealChannel}
                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                  </a>
                  <p className="text-xs text-[#888680] mt-2">
                    This is the same public appeal URL anyone can use. We submit through this channel with your authorization for Appeal Package and Priority Support clients.
                  </p>
                </div>
              </div>
            </section>

            {/* FAQ section (paired with FAQPage schema above) */}
            <section className="py-12 md:py-16 bg-[#302f2c]" data-testid={`platform-${platform}-faqs`}>
              <div className="max-w-3xl mx-auto px-5 md:px-10">
                <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-3 flex items-center gap-3">
                  <HelpCircle className="w-7 h-7" />
                  {data.name} Appeal FAQ
                </h2>
                <p className="text-sm text-[#888680] mb-10">
                  The questions we get asked most about {data.name} appeals — answered factually based on the platform's published documentation.
                </p>
                <div className="space-y-4">
                  {enriched.faqs.map((faq, idx) => (
                    <details
                      key={faq.q}
                      className="bg-[#1a1c1b] border border-[#3f4816]/40 rounded-xl p-5 group"
                      data-testid={`platform-${platform}-faq-${idx}`}
                    >
                      <summary className="cursor-pointer font-semibold text-[#dfddd6] flex items-start gap-3 list-none">
                        <span className="text-[#d9fb06] font-bold flex-shrink-0">Q.</span>
                        <span className="flex-1">{faq.q}</span>
                      </summary>
                      <p className="mt-3 ml-7 text-sm text-[#888680] leading-relaxed">{faq.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            {/* Related articles (internal link mesh for SEO) */}
            {enriched.relatedArticles && enriched.relatedArticles.length > 0 && (
              <section className="py-12 md:py-16" data-testid={`platform-${platform}-related`}>
                <div className="max-w-5xl mx-auto px-5 md:px-10">
                  <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-3">
                    Read More on {data.name} Appeals
                  </h2>
                  <p className="text-sm text-[#888680] mb-8">
                    Deep reference articles for {data.name} cases — written by our founder, sourced from {data.parent}'s published documentation.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {enriched.relatedArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/blog/${article.slug}`}
                        className="rounded-xl border border-[#3f4816]/40 bg-[#302f2c] p-5 hover:border-[#d9fb06]/50 hover:bg-[#302f2c]/80 transition group"
                      >
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-[#dfddd6] font-semibold group-hover:text-[#d9fb06] transition">
                              {article.title}
                            </p>
                            <p className="text-xs text-[#888680] mt-1">Reference article →</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* CTA Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-6">
              Ready to Get Your {data.name} Back?
            </h2>
            <p className="text-lg text-[#888680] mb-8">
              Submit your intake form now and our team will review your case within 24-48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#intake">
                <Button 
                  size="lg"
                  className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold text-lg px-8"
                >
                  Start Your Case - $99
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <p className="text-sm text-[#888680] mt-4">
              Priority service available for $499 (expedited preparation)
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-[#302f2c] border-t border-[#3f4816]/50">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <p className="text-xs text-[#888680] text-center">
              <strong>Disclaimer:</strong> Social Dispute Co. is not affiliated with {data.name} or {data.parent}. 
              We are an independent documentation preparation service. We do not guarantee outcomes. 
              Clients submit appeals through the platform&apos;s official public channel. On the $99 Case Review the client submits; on the $399 Appeal Package and $499 Priority Support we submit with the client&apos;s written authorization. Platform decisions are outside our control.
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

// Export individual platform pages
export const InstagramAppealPage = () => <PlatformServicePage platform="instagram" />;
export const FacebookAppealPage = () => <PlatformServicePage platform="facebook" />;
export const TikTokAppealPage = () => <PlatformServicePage platform="tiktok" />;
export const YouTubeAppealPage = () => <PlatformServicePage platform="youtube" />;
export const TwitterAppealPage = () => <PlatformServicePage platform="twitter" />;
export const LinkedInAppealPage = () => <PlatformServicePage platform="linkedin" />;
export const UberEatsAppealPage = () => <PlatformServicePage platform="ubereats" />;
export const GoogleBusinessAppealPage = () => <PlatformServicePage platform="google-business" />;
export const FacebookBusinessManagerAppealPage = () => <PlatformServicePage platform="facebook-business-manager" />;
export const InstagramAdAccountAppealPage = () => <PlatformServicePage platform="instagram-ad-account" />;

// Keep old exports for backwards compatibility during transition
export const InstagramRecoveryPage = InstagramAppealPage;
export const FacebookRecoveryPage = FacebookAppealPage;
export const TikTokRecoveryPage = TikTokAppealPage;
export const YouTubeRecoveryPage = YouTubeAppealPage;
export const TwitterRecoveryPage = TwitterAppealPage;
export const LinkedInRecoveryPage = LinkedInAppealPage;
export const UberEatsRecoveryPage = UberEatsAppealPage;

export default PlatformServicePage;
